/**
 * Frontend validation utilities for vendor data management.
 * Mirrors backend validators from src/utils/validators.ts.
 *
 * Each validator returns { valid: boolean; error?: string }.
 * All functions are pure with no side effects.
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates PAN (Permanent Account Number) format.
 * Must be exactly 10 characters matching [A-Z]{5}[0-9]{4}[A-Z]{1}.
 */
export function validatePAN(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: 'PAN is required' };
  }

  if (value.length !== 10) {
    return { valid: false, error: 'PAN must be exactly 10 characters' };
  }

  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!panRegex.test(value)) {
    return { valid: false, error: 'PAN must match format [A-Z]{5}[0-9]{4}[A-Z]{1}' };
  }

  return { valid: true };
}

/**
 * Validates GSTIN (Goods and Services Tax Identification Number) format.
 * Must be exactly 15 characters matching [0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}.
 */
export function validateGSTIN(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: 'GSTIN is required' };
  }

  if (value.length !== 15) {
    return { valid: false, error: 'GSTIN must be exactly 15 characters' };
  }

  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  if (!gstinRegex.test(value)) {
    return { valid: false, error: 'GSTIN must match format [0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}' };
  }

  return { valid: true };
}

/**
 * Validates employee count.
 * Must be an integer >= 0.
 */
export function validateEmployeeCount(value: number): ValidationResult {
  if (value === null || value === undefined) {
    return { valid: false, error: 'Employee count is required' };
  }

  if (typeof value !== 'number' || isNaN(value)) {
    return { valid: false, error: 'Employee count must be a number' };
  }

  if (!Number.isInteger(value)) {
    return { valid: false, error: 'Employee count must be an integer' };
  }

  if (value < 0) {
    return { valid: false, error: 'Employee count must be 0 or greater' };
  }

  return { valid: true };
}

/**
 * Validates year of founding.
 * Must be an integer between 1800 and the current year (inclusive).
 */
export function validateYearOfFounding(value: number): ValidationResult {
  if (value === null || value === undefined) {
    return { valid: false, error: 'Year of founding is required' };
  }

  if (typeof value !== 'number' || isNaN(value)) {
    return { valid: false, error: 'Year of founding must be a number' };
  }

  if (!Number.isInteger(value)) {
    return { valid: false, error: 'Year of founding must be an integer' };
  }

  const currentYear = new Date().getFullYear();
  if (value < 1800 || value > currentYear) {
    return { valid: false, error: `Year of founding must be between 1800 and ${currentYear}` };
  }

  return { valid: true };
}

/**
 * Validates number of locations.
 * Must be an integer >= 1.
 */
export function validateLocationCount(value: number): ValidationResult {
  if (value === null || value === undefined) {
    return { valid: false, error: 'Number of locations is required' };
  }

  if (typeof value !== 'number' || isNaN(value)) {
    return { valid: false, error: 'Number of locations must be a number' };
  }

  if (!Number.isInteger(value)) {
    return { valid: false, error: 'Number of locations must be an integer' };
  }

  if (value < 1) {
    return { valid: false, error: 'Number of locations must be at least 1' };
  }

  return { valid: true };
}
